/**
 * Pure derivation helpers for MappingPhaseSection (import-wizard.tsx).
 * Extracted to keep import-wizard.tsx under the ESLint max-lines limit and to
 * keep each derivation's `??`/optional-chain count out of the orchestrating
 * component's own cyclomatic complexity.
 */
import type {
  ColumnMappingItem,
  ColumnTransformSuggestion,
  FieldOptionItem,
  ImportColumnsResponse,
  ImportJob,
  ImportJobStatus,
  SupplierOption,
} from '@ooh/contracts';
import { resolveFieldKeysFromAdapterColumns } from '@ooh/contracts';
import { collectMappedFieldKeys, stripRequiredSuffix } from './collect-mapped-field-keys';

/**
 * Returns field keys of required fields not covered by suggestedMapping (excluding 'supplier',
 * which is provided via the wizard's separate supplier picker instead of a column mapping).
 * A field counts as covered via a plain `mappedTo` OR a pattern-transform per-blank target —
 * see collectMappedFieldKeys for why (custom-supplier-import-transforms: splitting a required
 * field like latitude/longitude via a combined-column pattern must satisfy this check).
 */
export function computeMissingRequiredFieldKeys(
  suggestedMapping: ColumnMappingItem[],
  effectiveFieldOptions: FieldOptionItem[],
): string[] {
  const mappedKeys = collectMappedFieldKeys(suggestedMapping);
  return effectiveFieldOptions
    .filter((o) => o.required === true && !mappedKeys.has(o.key) && o.key !== 'supplier')
    .map((o) => o.key);
}

/**
 * Filter and annotate fieldOptions using adapter column lists.
 * When adapterColumns is provided, only show columns the adapter knows about,
 * and mark adapter-required columns as required=true.
 */
export function buildFilteredFieldOptions(
  baseOptions: FieldOptionItem[],
  adapterColumns?: { required: string[]; optional: string[] },
): FieldOptionItem[] {
  if (!adapterColumns) return baseOptions;

  const requiredSet = new Set(adapterColumns.required);
  const optionalSet = new Set(adapterColumns.optional);
  const allAdapterLabels = new Set([...requiredSet, ...optionalSet]);

  const knownFieldKeys = new Set(baseOptions.map((o) => o.key));
  const requiredKeySet = resolveFieldKeysFromAdapterColumns(
    adapterColumns.required,
    knownFieldKeys,
  );
  const optionalKeySet = resolveFieldKeysFromAdapterColumns(
    adapterColumns.optional,
    knownFieldKeys,
  );
  const allAdapterKeys = new Set([...requiredKeySet, ...optionalKeySet]);

  const isIncluded = (o: FieldOptionItem) =>
    allAdapterLabels.has(stripRequiredSuffix(o.label)) || allAdapterKeys.has(o.key);
  const isRequired = (o: FieldOptionItem) =>
    requiredSet.has(stripRequiredSuffix(o.label)) || requiredKeySet.has(o.key);

  return baseOptions.filter(isIncluded).map((o) => (isRequired(o) ? { ...o, required: true } : o));
}

export interface ColumnMappingDerivedProps {
  hasSavedMapping: boolean;
  supplierName: string | undefined;
  columnMappings: ColumnMappingItem[];
  suppliers: SupplierOption[];
  columnSamples: Record<string, string[]> | undefined;
  columnDistinctValues: Record<string, string[]> | undefined;
  transformSuggestions: ColumnTransformSuggestion[] | undefined;
}

/** Derive all props needed by MappingPhase from the columns API response (excluding missingRequiredFieldKeys
 * which depends on effectiveFieldOptions and is computed separately in MappingPhaseSection). */
export function deriveColumnMappingProps(
  columnsData: ImportColumnsResponse | undefined,
  selectedSupplierId: string | null,
): ColumnMappingDerivedProps {
  if (!columnsData) {
    return {
      hasSavedMapping: false,
      supplierName: undefined,
      columnMappings: [],
      suppliers: [],
      columnSamples: undefined,
      columnDistinctValues: undefined,
      transformSuggestions: undefined,
    };
  }
  const { savedMapping, suggestedMapping, suppliers } = columnsData;
  const hasSavedMapping = savedMapping != null && savedMapping.length > 0;
  const supplierInList = suppliers.find((s) => s.id === selectedSupplierId);
  // The picked supplier's name must survive the columns refetch triggered by
  // the pick itself: MappingPhase is keyed on selectedSupplierId, so the pick
  // remounts it and the remounted phase re-seeds its supplier combobox from
  // supplierName. Gating this on hasSavedMapping blanked the combobox for
  // every supplier WITHOUT a saved mapping and forced the operator to pick
  // the same supplier twice (CU-86cb1nr0f).
  const supplierName = supplierInList != null ? supplierInList.name : undefined;
  return {
    hasSavedMapping,
    supplierName,
    columnMappings: hasSavedMapping ? savedMapping! : suggestedMapping,
    suppliers,
    columnSamples: columnsData.columnSamples,
    columnDistinctValues: columnsData.columnDistinctValues,
    transformSuggestions: columnsData.transformSuggestions,
  };
}

/**
 * Resolves which wizard phase to jump to when `GET /import/:id/columns` 409s because the
 * job already left the mapping step (e.g. a stale `?phase=mapping` URL revisited after
 * confirm, or a double-submit) — mirrors the confirm-mapping mutation's existing 409
 * handling (`onPhaseChange('validation', importJobId)`), generalized to use the job's
 * actual status instead of always assuming 'validation', so a job that's already
 * processing/completed lands on its real current phase instead of a validation screen
 * for a job that no longer has anything to validate.
 */
export function resolvePhaseFromJobStatus(
  status: ImportJobStatus | undefined,
): 'validation' | 'progress' | 'result' {
  if (status === 'processing') return 'progress';
  if (status === 'completed' || status === 'failed') return 'result';
  return 'validation';
}

export interface MappingPhaseSourceExtras {
  manualFallbackMapping: ColumnMappingItem[] | undefined;
  headerRowIndex: number | undefined;
  sheets: string[] | undefined;
  selectedSheet: string | undefined;
  fileName: string | undefined;
  estimatedRows: number | undefined;
}

function deriveSelectedSheet(
  sheets: string[] | undefined,
  selectedSheetName: string | undefined,
): string | undefined {
  return selectedSheetName ?? sheets?.[0];
}

/** Derives the source-bar/banner-clear props (column-mapping-ui: sheet picker, header note,
 * "clear saved config" fallback). */
export function deriveMappingPhaseSourceExtras(
  columnsData: ImportColumnsResponse | undefined,
  jobStatus: ImportJob | undefined,
  selectedSheetName: string | undefined,
): MappingPhaseSourceExtras {
  const sheets = columnsData?.sheets;
  return {
    manualFallbackMapping: columnsData?.suggestedMapping,
    headerRowIndex: columnsData?.headerRowIndex,
    sheets,
    selectedSheet: deriveSelectedSheet(sheets, selectedSheetName),
    fileName: jobStatus?.originalName ?? undefined,
    estimatedRows: jobStatus?.totalRows ?? undefined,
  };
}
